// src/Dashboard/pages/teacher/Belts/hooks/usePassageCeintureDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getGroupsList } from "../../../../../services/groupServices";
import { getAllAdherentsListWithGroup } from "../../../../../services/adherentServices";

import {
  addBeltHistoryBulk,
  getLatestBeltHistoriesByAdherents,
  getBeltsList,
} from "../../../../../services/beltServices";

import { getSettings } from "../../../../../services/settingsServices";

import {
  buildSeasonOptions,
  calculateBeltStats,
  filterAdherentsForBelts,
  getTodayInputDate,
  sortAdherentsForBelts,
} from "../utils/beltDashboardUtils";

const defaultFilters = {
  query: "",
  season: "",
  group: "",
  belt: "",
  historyStatus: "All",
  sort: "name_asc",
};

export function usePassageCeintureDashboard() {
  const [groups, setGroups] = useState([]);
  const [adherents, setAdherents] = useState([]);
  const [belts, setBelts] = useState([]);
  const [beltHistories, setBeltHistories] = useState({});
  const [seasons, setSeasons] = useState([]);

  const [filters, setFilters] = useState(defaultFilters);

  const [loading, setLoading] = useState(true);
  const [bulkSaving, setBulkSaving] = useState(false);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBeltId, setBulkBeltId] = useState("");
  const [bulkDate, setBulkDate] = useState(getTodayInputDate());

  const [openedAdherent, setOpenedAdherent] = useState(null);

  const loadCoreData = useCallback(async () => {
    try {
      setLoading(true);

      const [settingsData, groupList, adherentsList, beltsList] =
        await Promise.all([
          getSettings(),
          getGroupsList(),
          getAllAdherentsListWithGroup(),
          getBeltsList(),
        ]);

      const settings = Array.isArray(settingsData)
        ? settingsData[0] || {}
        : settingsData || {};

      const activeSeason = settings?.saison_active || "";

      const normalizedGroups = Array.isArray(groupList) ? groupList : [];
      const normalizedAdherents = Array.isArray(adherentsList)
        ? adherentsList
        : [];
      const normalizedBelts = Array.isArray(beltsList) ? beltsList : [];

      setGroups(normalizedGroups);
      setAdherents(normalizedAdherents);
      setBelts(normalizedBelts);

      setSeasons(buildSeasonOptions(activeSeason));

      setFilters((prev) => ({
        ...prev,
        season: prev.season || activeSeason || "",
      }));
    } catch (error) {
      console.error("Erreur chargement ceintures :", error);
      toast.error("Erreur lors du chargement des passages de grades.");

      setGroups([]);
      setAdherents([]);
      setBelts([]);
      setBeltHistories({});
      setSeasons([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshHistories = useCallback(async () => {
    try {
      setLoading(true);

      const adherentIds = adherents
        .map((adherent) => adherent?.id)
        .filter(Boolean);

      if (!adherentIds.length) {
        setBeltHistories({});
        return;
      }

      const historiesMap = await getLatestBeltHistoriesByAdherents(adherentIds);

      setBeltHistories(historiesMap || {});
    } catch (error) {
      console.error("Erreur chargement historiques :", error);
      toast.error("Erreur lors du chargement des ceintures actuelles.");
      setBeltHistories({});
    } finally {
      setLoading(false);
    }
  }, [adherents]);

  const refreshAll = useCallback(async () => {
    await loadCoreData();
  }, [loadCoreData]);

  useEffect(() => {
    loadCoreData();
  }, [loadCoreData]);

  useEffect(() => {
    if (!adherents.length) {
      setBeltHistories({});
      return;
    }

    refreshHistories();
  }, [adherents, refreshHistories]);

  const filteredAdherents = useMemo(() => {
    return sortAdherentsForBelts(
      filterAdherentsForBelts({
        adherents,
        histories: beltHistories,
        filters,
      }),
      filters.sort,
    );
  }, [adherents, beltHistories, filters]);

  useEffect(() => {
    const visibleIds = new Set(
      filteredAdherents.map((adherent) => adherent.id),
    );

    setSelectedIds((prev) => {
      const next = new Set();

      prev.forEach((id) => {
        if (visibleIds.has(id)) {
          next.add(id);
        }
      });

      return next;
    });
  }, [filteredAdherents]);

  const stats = useMemo(() => {
    return calculateBeltStats({
      allAdherents: adherents,
      filteredAdherents,
      histories: beltHistories,
    });
  }, [adherents, filteredAdherents, beltHistories]);

  const allChecked =
    filteredAdherents.length > 0 &&
    filteredAdherents.every((adherent) => selectedIds.has(adherent.id));

  const toggleAll = (checked) => {
    if (checked) {
      setSelectedIds(new Set(filteredAdherents.map((adherent) => adherent.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const toggleOne = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }

      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setBulkBeltId("");
    setBulkDate(getTodayInputDate());
  };

  const applyBulk = async () => {
    if (selectedIds.size === 0) {
      toast.warn("Sélectionnez au moins un adhérent.");
      return;
    }

    if (!bulkBeltId) {
      toast.warn("Choisissez une ceinture.");
      return;
    }

    if (!bulkDate) {
      toast.warn("Choisissez une date de passage.");
      return;
    }

    const adherentIds = Array.from(selectedIds).map(Number);
    const beltId = Number(bulkBeltId);

    try {
      setBulkSaving(true);

      const result = await addBeltHistoryBulk({
        adherent_ids: adherentIds,
        belt_id: beltId,
        date_passage: bulkDate,
      });

      const created = Number(result?.created || 0);
      const skipped = Number(result?.skipped || 0);

      if (created > 0) {
        toast.success(
          `${created} passage${created > 1 ? "s" : ""} créé${
            created > 1 ? "s" : ""
          }${skipped ? ` • ${skipped} déjà existant(s)` : ""}.`,
        );
      } else if (skipped > 0) {
        toast.info("Tous les passages existaient déjà pour cette date.");
      } else {
        toast.warn("Aucun passage n’a été créé.");
      }

      clearSelection();
      await refreshHistories();
    } catch (error) {
      console.error("Erreur passage groupé :", error);
      toast.error("Erreur lors de l’application du passage groupé.");
    } finally {
      setBulkSaving(false);
    }
  };

  const openDrawer = (adherent) => {
    setOpenedAdherent(adherent);
  };

  const closeDrawer = () => {
    setOpenedAdherent(null);
  };

  return {
    groups,
    adherents,
    belts,
    beltHistories,
    filteredAdherents,
    filters,
    setFilters,
    seasons,
    stats,
    loading,

    selectedIds,
    allChecked,
    toggleAll,
    toggleOne,
    clearSelection,

    bulkBeltId,
    setBulkBeltId,
    bulkDate,
    setBulkDate,
    bulkSaving,
    applyBulk,

    openedAdherent,
    openDrawer,
    closeDrawer,

    refreshHistories,
    refreshAll,
  };
}